import {
  MeQuery,
  UsersPermissionsUserQuery,
  useMeLazyQuery,
  useUsersPermissionsUserQuery,
} from "@/generated";
import { useRouter } from "next/router";
import React, { createContext, useState, useEffect, useContext } from "react";
import { useAuth } from "@/utils/hooks/useAuth";
import routes from "@/utils/routes";
import { ApolloError } from "@apollo/client";

interface ProviderProps {
  children: React.ReactNode;
  authOnly: boolean;
  anonymousOnly: boolean;
}

const UserContext = createContext<{
  user?: MeQuery["me"];
  userAttributes?: UsersPermissionsUserQuery | null;
  userLoading?: boolean;
  userError?: ApolloError;
}>({});

const UserProvider = ({ children, authOnly, anonymousOnly }: ProviderProps) => {
  const { token } = useAuth();
  const [getMe, { data, loading }] = useMeLazyQuery();

  const [user, setUser] = useState<MeQuery["me"] | undefined>(null);
  const [cachedToken, setCachedToken] = useState<string>();
  const router = useRouter();

  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useUsersPermissionsUserQuery({
    skip: !user?.id || user?.id === undefined,
    fetchPolicy: "cache-and-network",
    variables: { id: user?.id ?? "" },
  });

  useEffect(() => {
    if (loading) return;

    if (token) {
      if (token !== cachedToken) {
        getMe({
          onCompleted: (data) => {
            setUser(data?.me);
          },
          onError: () => setUser(undefined),
        });
        setCachedToken(token);
      }
    } else {
      setUser(undefined);
    }
  }, [cachedToken, data?.me, getMe, loading, token]);

  if (user === null) {
    return <></>;
  }
  if (user === undefined && authOnly) {
    router.push(routes.user.register);
    return <></>;
  }
  if (user?.id && anonymousOnly) {
    router.push(router?.query?.backURL?.toString() ?? routes.home);
    return <></>;
  }

  return (
    <UserContext.Provider
      value={{
        user,
        userAttributes: userData ?? null,
        userLoading,
        userError,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const { user, userAttributes, userLoading, userError } =
    useContext(UserContext);

  return { user, userAttributes, userLoading, userError };
};

export default UserProvider;
